/**
 * Where every stored object lives (docs/16-STORAGE.md). One builder for both
 * buckets, so that everything a church owns sits under one prefix,
 * `accounts/<orgId>/`, and deleting a church is deleting that prefix.
 *
 *   files bucket (private: the API proxies every read)
 *     accounts/<orgId>/church/people/<personId>/photo/<uuid>.<ext>
 *     accounts/<orgId>/church/people/<personId>/fields/<fieldId>/<uuid>
 *     accounts/<orgId>/church/groups/<groupId>/image/<uuid>.<ext>
 *     accounts/<orgId>/church/groups/<groupId>/resources/<uuid>
 *     accounts/<orgId>/church/events/<eventId>/image/<uuid>.<ext>
 *     accounts/<orgId>/church/events/<eventId>/files/<uuid>
 *     accounts/<orgId>/services/plans/<planId>/files/<uuid>
 *     accounts/<orgId>/services/plans/<planId>/items/<itemId>/<uuid>
 *     accounts/<orgId>/services/songs/<songId>/files/<uuid>
 *     accounts/<orgId>/services/songs/<songId>/artwork/<uuid>.<ext>
 *     accounts/<orgId>/services/songs/<songId>/audio/<uuid>.<ext>
 *     accounts/<orgId>/services/arrangements/<arrangementId>/files/<uuid>
 *     accounts/<orgId>/services/series/<seriesId>/art/<uuid>.<ext>
 *     accounts/<orgId>/services/series/<seriesId>/files/<uuid>
 *     accounts/<orgId>/exports/<exportId>.zip
 *
 *   media bucket (ForStream; players' media public through its domain)
 *     accounts/<orgId>/stream/sessions/<sessionId>/segments/seg-NNNNNN.ts
 *     accounts/<orgId>/stream/sessions/<sessionId>/renditions/r<height>/seg-NNNNNN.ts
 *     accounts/<orgId>/stream/sessions/<sessionId>/dub/<lang>/<ms>.mp3
 *     accounts/<orgId>/stream/sessions/<sessionId>/dub/reference-<ms>.mp3
 *     accounts/<orgId>/stream/sessions/<sessionId>/clips/<clipId>/<vertical|wide>.mp4
 *     accounts/<orgId>/stream/uploads/<videoId>/source<.ext>
 *     accounts/<orgId>/stream/thumbnails/<videoId>.jpg
 *     accounts/<orgId>/stream/fallback/<assetId>/source<.ext>
 *     accounts/<orgId>/stream/fallback/<assetId>/variants/<label>.ts
 *     accounts/<orgId>/stream/branding/<uuid>.<ext>
 *     platform/stream/fallback/builtin/variants/<label>.ts   (the bars: nobody's)
 *
 * Ids are the immutable UUIDs, never names or slugs: a church renames itself,
 * two churches share a name, and a name is personal data in a path. A file's
 * own name is a fresh UUIDv7 (its original name lives in its row). Every id
 * and name is checked here, so no key can climb out of its folder.
 *
 * Pure: no platform APIs beyond `crypto.getRandomValues`.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const UUID_RE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const EXT = /^[a-z0-9]{1,8}$/
const LANG = /^[A-Za-z]{2,8}(-[A-Za-z0-9]{1,8})*$/
const LANG_RE = '[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*'
/** A fallback variant's profile: `1920x1080p30-1`. */
const LABEL = /^\d{1,5}x\d{1,5}p\d{1,6}-\d{1,6}$/
const LABEL_RE = '\\d{1,5}x\\d{1,5}p\\d{1,6}-\\d{1,6}'

/** Thrown for an id or name that cannot be part of a key: a bug in the caller, not input. */
export class StorageKeyError extends Error {}

function id(value: string, what: string): string {
  if (!UUID.test(value))
    throw new StorageKeyError(`${what} is not a UUID: ${JSON.stringify(value)}`)
  return value
}

function count(value: number, what: string): number {
  if (!Number.isSafeInteger(value) || value < 0)
    throw new StorageKeyError(`${what} is not a whole number: ${value}`)
  return value
}

/** `"png"`, `".png"`, `""` or undefined → `".png"` or `""`. Lower case, at most 8 characters. */
function extension(ext: string | undefined): string {
  const bare = (ext ?? '').replace(/^\./, '').toLowerCase()
  if (bare === '') return ''
  if (!EXT.test(bare)) throw new StorageKeyError(`not a file extension: ${JSON.stringify(ext)}`)
  return `.${bare}`
}

/** A file extension from a file's name (`"Sermon.MP4"` → `".mp4"`), or `""`. */
export function extensionOf(fileName: string): string {
  const match = /\.([A-Za-z0-9]{1,8})$/.exec(fileName)
  return match?.[1] ? `.${match[1].toLowerCase()}` : ''
}

/** The extension an image key gets from its content type (photos, pictures, art, logos). */
export const imageExtension = (contentType: string) =>
  contentType === 'image/png' ? 'png' : contentType === 'image/webp' ? 'webp' : 'jpg'

/**
 * A UUIDv7 (RFC 9562): 48 bits of milliseconds, then random. Object names
 * sort by when they were made, and none can be guessed.
 */
export function uuidv7(now = Date.now()): string {
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  let ms = Math.max(0, Math.floor(now))
  for (let i = 5; i >= 0; i--) {
    b[i] = ms % 256
    ms = Math.floor(ms / 256)
  }
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x70
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** A new object's name in a folder: a fresh UUIDv7 and the extension, if any. */
const fresh = (prefix: string, ext?: string) => `${prefix}${uuidv7()}${extension(ext)}`

// ---- the account -------------------------------------------------------------

/** Everything one church owns, in either bucket. Deleting a church deletes this. */
export const accountPrefix = (orgId: string) => `accounts/${id(orgId, 'orgId')}/`

/** What is not any church's: the platform's own objects (the built-in fallback bars). */
export const PLATFORM_PREFIX = 'platform/'

/**
 * Whether `key` is an object inside the folder `prefix`: below it, and with
 * no part that could climb out or name something else (`..`, `.`, an empty
 * part, a leading `/`, a backslash, a control character). The test every
 * upload confirm makes before it believes a key the client hands back.
 */
export function isUnder(key: string, prefix: string): boolean {
  if (typeof key !== 'string' || typeof prefix !== 'string') return false
  if (!prefix.endsWith('/') || !isCleanPath(prefix)) return false
  if (!key.startsWith(prefix) || key.length === prefix.length) return false
  return isCleanPath(key) && !key.endsWith('/')
}

function isCleanPath(path: string): boolean {
  if (path === '' || path.startsWith('/') || path.length > 1024) return false
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return false
  const parts = path.endsWith('/') ? path.slice(0, -1).split('/') : path.split('/')
  return parts.every((part) => part !== '' && part !== '.' && part !== '..')
}

/** The church whose object this is (`accounts/<orgId>/…`), or null. */
export function accountOf(key: string): string | null {
  const match = new RegExp(`^accounts/(${UUID_RE})/`, 'i').exec(key)
  return match?.[1] && isCleanPath(key) ? match[1] : null
}

/**
 * A key's last part: the image URL's `?v=`. A new picture is a new name, so
 * caches never serve the old one.
 */
export const keyVersion = (key: string | null | undefined): string | null =>
  key ? (key.split('/').at(-1) ?? null) : null

// ---- files bucket: ForChurch ------------------------------------------------

const church = (orgId: string) => `${accountPrefix(orgId)}church/`
const services = (orgId: string) => `${accountPrefix(orgId)}services/`

export const personPhotoPrefix = (orgId: string, personId: string) =>
  `${church(orgId)}people/${id(personId, 'personId')}/photo/`
export const personPhotoKey = (orgId: string, personId: string, ext: string) =>
  fresh(personPhotoPrefix(orgId, personId), ext)

export const personFieldFilePrefix = (orgId: string, personId: string, fieldId: string) =>
  `${church(orgId)}people/${id(personId, 'personId')}/fields/${id(fieldId, 'fieldId')}/`
export const personFieldFileKey = (orgId: string, personId: string, fieldId: string) =>
  fresh(personFieldFilePrefix(orgId, personId, fieldId))

export const groupImagePrefix = (orgId: string, groupId: string) =>
  `${church(orgId)}groups/${id(groupId, 'groupId')}/image/`
export const groupImageKey = (orgId: string, groupId: string, ext: string) =>
  fresh(groupImagePrefix(orgId, groupId), ext)

export const groupResourcePrefix = (orgId: string, groupId: string) =>
  `${church(orgId)}groups/${id(groupId, 'groupId')}/resources/`
export const groupResourceKey = (orgId: string, groupId: string) =>
  fresh(groupResourcePrefix(orgId, groupId))

export const eventImagePrefix = (orgId: string, eventId: string) =>
  `${church(orgId)}events/${id(eventId, 'eventId')}/image/`
export const eventImageKey = (orgId: string, eventId: string, ext: string) =>
  fresh(eventImagePrefix(orgId, eventId), ext)

export const eventFilePrefix = (orgId: string, eventId: string) =>
  `${church(orgId)}events/${id(eventId, 'eventId')}/files/`
export const eventFileKey = (orgId: string, eventId: string) =>
  fresh(eventFilePrefix(orgId, eventId))

// ---- files bucket: ForServices ----------------------------------------------

/** A plan's own folder: its files and its items' files. */
export const planPrefix = (orgId: string, planId: string) =>
  `${services(orgId)}plans/${id(planId, 'planId')}/`
export const planFilePrefix = (orgId: string, planId: string) =>
  `${planPrefix(orgId, planId)}files/`
export const planFileKey = (orgId: string, planId: string, ext?: string) =>
  fresh(planFilePrefix(orgId, planId), ext)

export const planItemFilePrefix = (orgId: string, planId: string, itemId: string) =>
  `${planPrefix(orgId, planId)}items/${id(itemId, 'itemId')}/`
export const planItemFileKey = (orgId: string, planId: string, itemId: string, ext?: string) =>
  fresh(planItemFilePrefix(orgId, planId, itemId), ext)

export const songFilePrefix = (orgId: string, songId: string) =>
  `${services(orgId)}songs/${id(songId, 'songId')}/files/`
export const songFileKey = (orgId: string, songId: string, ext?: string) =>
  fresh(songFilePrefix(orgId, songId), ext)

/**
 * A song's own artwork and full recording, uploaded by its church
 * (`song_media`, docs/15): each kind a folder of its own beside the song's
 * files, so neither confirm accepts the other's key. Never Apple's.
 */
export const songArtworkPrefix = (orgId: string, songId: string) =>
  `${services(orgId)}songs/${id(songId, 'songId')}/artwork/`
export const songArtworkKey = (orgId: string, songId: string, ext: string) =>
  fresh(songArtworkPrefix(orgId, songId), ext)

export const songAudioPrefix = (orgId: string, songId: string) =>
  `${services(orgId)}songs/${id(songId, 'songId')}/audio/`
export const songAudioKey = (orgId: string, songId: string, ext: string) =>
  fresh(songAudioPrefix(orgId, songId), ext)

export const arrangementFilePrefix = (orgId: string, arrangementId: string) =>
  `${services(orgId)}arrangements/${id(arrangementId, 'arrangementId')}/files/`
export const arrangementFileKey = (orgId: string, arrangementId: string, ext?: string) =>
  fresh(arrangementFilePrefix(orgId, arrangementId), ext)

export const seriesArtPrefix = (orgId: string, seriesId: string) =>
  `${services(orgId)}series/${id(seriesId, 'seriesId')}/art/`
export const seriesArtKey = (orgId: string, seriesId: string, ext: string) =>
  fresh(seriesArtPrefix(orgId, seriesId), ext)

export const seriesFilePrefix = (orgId: string, seriesId: string) =>
  `${services(orgId)}series/${id(seriesId, 'seriesId')}/files/`
export const seriesFileKey = (orgId: string, seriesId: string, ext?: string) =>
  fresh(seriesFilePrefix(orgId, seriesId), ext)

// Images an AI made for the church (docs/17-AI-CREATIVE.md): a series' art
// directions and their slide backgrounds, a plan's quote-card backgrounds. A
// folder of their own beside the owner's art and files, so no confirm accepts
// one for the other.
export const seriesAiImagePrefix = (orgId: string, seriesId: string) =>
  `${services(orgId)}series/${id(seriesId, 'seriesId')}/ai/`
export const seriesAiImageKey = (orgId: string, seriesId: string, ext: string) =>
  fresh(seriesAiImagePrefix(orgId, seriesId), ext)
export const planAiImagePrefix = (orgId: string, planId: string) =>
  `${planPrefix(orgId, planId)}ai/`
export const planAiImageKey = (orgId: string, planId: string, ext: string) =>
  fresh(planAiImagePrefix(orgId, planId), ext)

// ---- files bucket: the account ----------------------------------------------

export const exportKey = (orgId: string, exportId: string) =>
  `${accountPrefix(orgId)}exports/${id(exportId, 'exportId')}.zip`

// ---- media bucket: ForStream -------------------------------------------------

const stream = (orgId: string) => `${accountPrefix(orgId)}stream/`

/** A broadcast's (or an upload's) folder: segments, renditions, dubbing. Deleting a session deletes it. */
export const streamSessionPrefix = (orgId: string, sessionId: string) =>
  `${stream(orgId)}sessions/${id(sessionId, 'sessionId')}/`

const segmentName = (sequence: number) =>
  `seg-${String(count(sequence, 'sequence')).padStart(6, '0')}.ts`

/** The only folder a producer's upload credentials can write. */
export const segmentPrefix = (orgId: string, sessionId: string) =>
  `${streamSessionPrefix(orgId, sessionId)}segments/`
export const segmentKey = (orgId: string, sessionId: string, sequence: number) =>
  `${segmentPrefix(orgId, sessionId)}${segmentName(sequence)}`
/** A segment's file name as producers write it: `seg-000042.ts` (six digits or more). */
export const SEGMENT_NAME = /^seg-\d{6,}\.ts$/

export const renditionKey = (
  orgId: string,
  sessionId: string,
  height: number,
  sequence: number,
) => {
  if (count(height, 'height') === 0) throw new StorageKeyError('a rendition has a height')
  return `${streamSessionPrefix(orgId, sessionId)}renditions/r${height}/${segmentName(sequence)}`
}

export const dubPrefix = (orgId: string, sessionId: string) =>
  `${streamSessionPrefix(orgId, sessionId)}dub/`
export const dubClipKey = (orgId: string, sessionId: string, lang: string, ms: number) => {
  if (!LANG.test(lang)) throw new StorageKeyError(`not a language: ${JSON.stringify(lang)}`)
  return `${dubPrefix(orgId, sessionId)}${lang}/${count(ms, 'ms')}.mp3`
}
/** The speaker's voice, cloned for dubbing: private (never on the public domain). */
export const dubReferenceKey = (orgId: string, sessionId: string, ms: number) =>
  `${dubPrefix(orgId, sessionId)}reference-${count(ms, 'ms')}.mp3`

/**
 * A sermon clip made from a recording (docs/stream/OPERATIONS.md, "Sermon clips"): in the
 * recording's folder, so it goes with the recording; private (downloads are signed).
 */
export const clipPrefix = (orgId: string, sessionId: string, clipId: string) =>
  `${streamSessionPrefix(orgId, sessionId)}clips/${id(clipId, 'clipId')}/`
export const clipKey = (
  orgId: string,
  sessionId: string,
  clipId: string,
  format: 'vertical' | 'wide',
) => {
  if (format !== 'vertical' && format !== 'wide')
    throw new StorageKeyError(`not a clip format: ${JSON.stringify(format)}`)
  return `${clipPrefix(orgId, sessionId, clipId)}${format}.mp4`
}

/** Every library upload's original still in storage (the uploads sweep lists it). */
export const uploadsPrefix = (orgId: string) => `${stream(orgId)}uploads/`
/** A library upload's original: private, deleted once processed. */
export const uploadPrefix = (orgId: string, videoId: string) =>
  `${uploadsPrefix(orgId)}${id(videoId, 'videoId')}/`
export const uploadSourceKey = (orgId: string, videoId: string, ext?: string) =>
  `${uploadPrefix(orgId, videoId)}source${extension(ext)}`

export const thumbnailKey = (orgId: string, videoId: string) =>
  `${stream(orgId)}thumbnails/${id(videoId, 'videoId')}.jpg`

/** One uploaded fallback video: its original and every converted copy. */
export const fallbackAssetPrefix = (orgId: string, assetId: string) =>
  `${stream(orgId)}fallback/${id(assetId, 'assetId')}/`
export const fallbackSourceKey = (orgId: string, assetId: string, ext?: string) =>
  `${fallbackAssetPrefix(orgId, assetId)}source${extension(ext)}`
export const fallbackVariantKey = (orgId: string, assetId: string, label: string) => {
  if (!LABEL.test(label)) throw new StorageKeyError(`not a profile label: ${JSON.stringify(label)}`)
  return `${fallbackAssetPrefix(orgId, assetId)}variants/${label}.ts`
}
/** The built-in colour bars, converted per profile: shared by every church, owned by none. */
export const builtinFallbackVariantKey = (label: string) => {
  if (!LABEL.test(label)) throw new StorageKeyError(`not a profile label: ${JSON.stringify(label)}`)
  return `${PLATFORM_PREFIX}stream/fallback/builtin/variants/${label}.ts`
}

export const brandingPrefix = (orgId: string) => `${stream(orgId)}branding/`
export const brandingKey = (orgId: string, ext: string) => fresh(brandingPrefix(orgId), ext)

/**
 * What players fetch from the media bucket's public domain: segments, their
 * renditions, dub clips and fallback variants. Nothing else — uploads,
 * thumbnails, voice references, fallback originals, logos — is ever served
 * there. The media domain's WAF rule (infra/stream.ts) allows the same shapes.
 */
export const PUBLIC_MEDIA_KEYS: readonly RegExp[] = [
  new RegExp(`^accounts/${UUID_RE}/stream/sessions/${UUID_RE}/segments/seg-\\d{6,}\\.ts$`),
  new RegExp(
    `^accounts/${UUID_RE}/stream/sessions/${UUID_RE}/renditions/r\\d{1,5}/seg-\\d{6,}\\.ts$`,
  ),
  new RegExp(`^accounts/${UUID_RE}/stream/sessions/${UUID_RE}/dub/${LANG_RE}/\\d+\\.mp3$`),
  new RegExp(`^accounts/${UUID_RE}/stream/fallback/${UUID_RE}/variants/${LABEL_RE}\\.ts$`),
  new RegExp(`^platform/stream/fallback/builtin/variants/${LABEL_RE}\\.ts$`),
]

export const isPublicMediaKey = (key: string) => PUBLIC_MEDIA_KEYS.some((re) => re.test(key))
